import { forwardRef, useRef, useState, type KeyboardEvent } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatarMesAno } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const COMPETENCIA = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Mes corrente (AAAA-MM), pelo relogio do navegador. So calendario, nenhum valor. */
export function competenciaDeHoje(agora: Date = new Date()): string {
  return `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}`;
}

/** Origem do ponto no mes: rateio salvo no app ou importado do XARP. */
export type MarcaMes = 'APP' | 'XARP';

export interface MonthPickerProps {
  /** Competencia escolhida (AAAA-MM). */
  value: string;
  onChange: (comp: string) => void;
  id?: string;
  /** Nome acessivel do botao quando o `<label htmlFor>` nao basta. */
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  onBlur?: () => void;
  disabled?: boolean;
  /** Pontos nos meses: chave AAAA-MM -> origem. So o ano visivel no popover aparece. */
  marcadores?: Record<string, MarcaMes>;
  /** Mostra a legenda ("salvo no app" / "importado do XARP") no popover. */
  legenda?: boolean;
  className?: string;
}

/**
 * Seletor de competencia (mes e ano): campo fechado com o mes por extenso; ao abrir, grade de 12 meses,
 * navegacao por ano e "Hoje". Setas movem o foco entre os meses; Esc fecha (Radix).
 */
export const MonthPicker = forwardRef<HTMLButtonElement, MonthPickerProps>(function MonthPicker(
  { value, onChange, id, 'aria-label': ariaLabel, 'aria-describedby': descrito, 'aria-invalid': invalido, onBlur, disabled, marcadores = {}, legenda = false, className },
  ref,
) {
  const valido = COMPETENCIA.test(value);
  const anoDoValor = Number(value.slice(0, 4));
  const [aberto, setAberto] = useState(false);
  const [ano, setAno] = useState(anoDoValor);
  const botoes = useRef<Array<HTMLButtonElement | null>>([]);
  const hoje = competenciaDeHoje();

  const texto = valido ? formatarMesAno(anoDoValor, Number(value.slice(5, 7))) : value;

  const mudarAberto = (proximo: boolean) => {
    // Ao abrir, o popover mostra o ano do valor escolhido.
    if (proximo) setAno(anoDoValor);
    setAberto(proximo);
  };

  const escolher = (mes: number) => {
    onChange(`${ano}-${String(mes).padStart(2, '0')}`);
    setAberto(false);
  };

  const escolherHoje = () => {
    onChange(competenciaDeHoje());
    setAberto(false);
  };

  const teclado = (e: KeyboardEvent<HTMLButtonElement>, indice: number) => {
    const passo = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowDown' ? 4 : e.key === 'ArrowUp' ? -4 : null;
    if (passo === null) return;
    e.preventDefault();
    botoes.current[(indice + passo + 12) % 12]?.focus();
  };

  return (
    <Popover open={aberto} onOpenChange={mudarAberto}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label={ariaLabel}
          aria-describedby={descrito}
          aria-invalid={invalido}
          onBlur={onBlur}
          className={cn('h-[34px] gap-2 rounded-[9px] bg-card px-2.5 text-small font-normal text-ink-body tabular focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none', className)}
        >
          <span>{texto}</span>
          <Calendar aria-hidden="true" className="size-4 text-ink-muted" />
        </Button>
      </PopoverTrigger>

      <PopoverContent align="start" aria-label="Escolher o mês" className="w-[300px] p-3">
        <div className="flex items-center justify-between gap-2">
          <Button type="button" variant="outline" size="icon-sm" aria-label="Ano anterior" onClick={() => setAno(ano - 1)}>
            <ChevronLeft aria-hidden="true" />
          </Button>
          <span aria-live="polite" className="tabular text-title font-semibold">
            {ano}
          </span>
          <Button type="button" variant="outline" size="icon-sm" aria-label="Próximo ano" onClick={() => setAno(ano + 1)}>
            <ChevronRight aria-hidden="true" />
          </Button>
        </div>

        <div role="group" aria-label="Meses" className="mt-3 grid grid-cols-4 gap-1.5">
          {MESES.map((rotulo, i) => {
            const mes = i + 1;
            const chave = `${ano}-${String(mes).padStart(2, '0')}`;
            const selecionado = chave === value;
            const marca = marcadores[chave];
            const atual = chave === hoje;
            return (
              <button
                key={rotulo}
                ref={(el) => {
                  botoes.current[i] = el;
                }}
                type="button"
                aria-pressed={selecionado}
                aria-current={atual ? 'date' : undefined}
                onClick={() => escolher(mes)}
                onKeyDown={(e) => teclado(e, i)}
                className={cn(
                  'flex h-11 flex-col items-center justify-center gap-1 rounded-[9px] border text-small font-medium focus-visible:shadow-focus focus-visible:outline-none',
                  selecionado ? 'border-primary bg-primary text-primary-foreground' : atual ? 'border-primary bg-card text-ink-body hover:bg-line-soft' : 'border-border bg-card text-ink-body hover:bg-line-soft',
                )}
              >
                <span>{rotulo}</span>
                {marca && (
                  <>
                    <span aria-hidden="true" className={cn('size-1.5 rounded-full', marca === 'XARP' ? 'bg-warning-dot' : 'bg-success-ink')} />
                    <span className="sr-only">{marca === 'XARP' ? 'rateio importado do XARP' : 'rateio salvo no app'}</span>
                  </>
                )}
              </button>
            );
          })}
        </div>

        {legenda && (
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-caption text-ink-muted">
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden="true" className="size-2 rounded-full bg-success-ink" />
              salvo no app
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden="true" className="size-2 rounded-full bg-warning-dot" />
              importado do XARP
            </span>
          </div>
        )}

        <div className="mt-3 flex justify-end">
          <Button type="button" variant="ghost" size="sm" onClick={escolherHoje}>
            Hoje
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
});
